// Deployment: its lists that open by address, and the rule for which stores are short
// of people. No React in here.
//
// The store lists follow the Month picker; the two lists of people (Undeployed
// Employees, Outside Approved – Redeploy) are always today's.
import { DEPLOYMENT_LISTS, DEPLOYMENT_LIST_SLUG, type DeploymentList } from '../../../navigation/dashboards';
import type { DashboardListInfo } from './types';

// One line per list: what it holds. Shown in page search. (Not on the dashboard: each
// list's own card says what it holds.)
const LIST_WORDS: Record<DeploymentList, { line: string; keywords?: string }> = {
  'Store Performance': { line: 'Every store that has someone deployed, weakest first. Open a row to move, swap or exit a person.' },
  'Bottom Box': { line: 'The lowest 3% of each manager’s team by six-month average score, with the store they stand in.', keywords: 'lowest scorers weakest' },
  'Undeployed Stores': { line: 'Stores with nobody, or fewer people than the store category needs.', keywords: 'vacant vacancy vacancies empty no staff understaffed short of people' },
  'MisDeployed Stores': { line: 'Stores where the person’s length of service does not fit what the store category asks for: too new for the store, or senior enough for a bigger one.', keywords: 'wrong store under-qualified over-qualified' },
  'Undeployed Employees': { line: 'Active people on the programme who are not placed at any store today.', keywords: 'bench not placed' },
  'Outside Approved – Redeploy': { line: 'People still working at a store marked outside the approved list, so they are waiting to be moved. Longest wait first.', keywords: 'exiting store waiting to be moved' },
};

/** Deployment's six lists, in the order of its strip. The names and slugs are navigation/dashboards' own. */
export const LISTS: DashboardListInfo[] = DEPLOYMENT_LISTS.map((name) => ({
  slug: DEPLOYMENT_LIST_SLUG[name],
  name,
  ...LIST_WORDS[name],
}));

// ── Stores short of people: the rule ─────────────────────────────────────────────
// Which stores are on Undeployed Stores is decided here and nowhere else in the browser:
// the page's list, pill and tile all come from storesShortOfPeople.
//
// The server keeps a second copy, for the home screen's "Stores short of people" number
// (it cannot import this file). The two are held together by one set of cases,
// apps/api/src/test-support/stores-short-cases.json, which both test suites run: change
// the rule on one side only and the other side's test fails.

/** What the rule reads of one store row. */
export interface StoreStaffing {
  /** The store's code. Rows that share one are one store. */
  code: string | null | undefined;
  /** The store's status as stored. Left out for rows already narrowed to open stores. */
  status?: string | null;
  /** The store category's minimum headcount as stored; anything that is not a whole number from 1 up counts as 1. */
  minimum: number | null | undefined;
  /** People still working at the store. */
  headcount: number;
}

// A DENYLIST, not an allowlist. The status column has no fixed set of values (production
// carries 'Notice' beside Active and Inactive, for a store that is closing but still
// trading), so only what definitely means CLOSED is left out and everything else stays
// in. Blank counts as open: the column is empty on older rows.
const CLOSED_STORE_STATUSES = new Set(['inactive']);

/** True for a store that is closed: it is not a vacancy, and is on no live list. */
export const isClosedStoreStatus = (status: string | null | undefined): boolean =>
  CLOSED_STORE_STATUSES.has((status ?? '').trim().toLowerCase());

/**
 * The code a store row is listed and summed under. Rows with no code all share one, as
 * the page has always drawn them, so together they are one row on the list.
 */
export const storeCodeKey = (code: string | null | undefined): string => code ?? '—';

/** The headcount a store needs: its category's minimum, or 1 where none (or none usable) is set. */
export const minimumPeople = (minimum: unknown): number =>
  (Number.isInteger(minimum) && Number(minimum) >= 1 ? Number(minimum) : 1);

/**
 * The stores short of people, one per store code, in the order their first row came:
 * closed stores left out, headcount summed over the rows that share a code, and the sum
 * BELOW the minimum of the code's first row. So a store with nobody is on it, and so is
 * one that needs two and has one.
 *
 * `store` is the code's first row; `headcount` is the sum the row is listed with.
 */
export function storesShortOfPeople<T>(
  stores: readonly T[],
  staffing: (store: T) => StoreStaffing,
): Array<{ store: T; headcount: number }> {
  const byCode = new Map<string, { store: T; headcount: number; minimum: number }>();
  for (const store of stores) {
    const { code, status, minimum, headcount } = staffing(store);
    if (isClosedStoreStatus(status)) continue;
    const key = storeCodeKey(code);
    const seen = byCode.get(key);
    if (seen) seen.headcount += headcount;
    else byCode.set(key, { store, headcount, minimum: minimumPeople(minimum) });
  }
  return [...byCode.values()]
    .filter((v) => v.headcount < v.minimum)
    .map(({ store, headcount }) => ({ store, headcount }));
}
